/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import PreviewIframe from '../Preview/PreviewIframe';
import VersionHistoryLeftPanel from './VersionHistoryLeftPanel';
import VersionHistoryWarningModal from './VersionHistoryWarningModal';
import BananatagButton from '../../componentLibrary/BananatagButton';
import * as htmlOutputActions from '../../actions/htmlOutput';
import * as versionHistoryActions from '../../actions/versionHistory';
import * as canvasActions from '../../actions/canvasBlocks';
import * as Styled from './styles';
import eventService from '../../common/eventService';
import areBlocksEqual from '../../common/areBlocksEqual';
import { ERR_VERSION_HISTORY_LOAD } from '../../constants/Errors';
import { request } from '../../common/request';
import configs from '../../constants/configs';

// The list and the canvas will not be updated when new versions are saved by other users in RTE
// When the user enters VERSION_HISTORY mode, they will be presented with a snapshot of what
// version history was when they clicked on the button. This is in order to avoid list and
// canvas updates while in VERSION_HISTORY mode. Changed to support RTE v1, this might change
// in the future to auto update the list and the canvas in future releases.
const VersionHistory = ({
    documentID,
    draftTemplateID,
    currentItem,
    latestSavedItem,
    isCurrentVersion,
    hasOtherConnectedClients,
    resetVersionHistory,
    htmlOutput,
    dispatch,
}) => {
    const [cachedVersions, setCachedVersions] = useState({});
    // when VersionHistory is mounted, we are saving a snapshot of the latest saved item
    // this is in order to rely on the latestSavedItem from the moment the user clicked on
    // the button to go to version history. Changed to support RTE v1, this might change
    // in the future to auto update the list and the canvas in future releases.
    const [latestSavedItemSnapshot] = useState(latestSavedItem);
    const [showWarningModal, setShowWarningModal] = useState(false);

    const loadHTMLForLatestSavedItem = useCallback(() => {
        (async () => {
            try {
                const { blocks, versionID } = latestSavedItemSnapshot;

                dispatch(htmlOutputActions.updatedLoadedStatus(false));
                const { htmlWithEmbeddedCustomFonts } = await request.convertHtml({
                    draftID: draftTemplateID,
                    blocks,
                    useEmbeddedCustomFonts: true,
                });
                dispatch(
                    versionHistoryActions.updateVersionHistoryItem({
                        ...latestSavedItemSnapshot,
                        htmlWithEmbeddedCustomFonts,
                    })
                );

                setCachedVersions({ [versionID]: { blocks, htmlWithEmbeddedCustomFonts } });
            } catch (err) {
                console.error(err);
                eventService.notify(err.customMessage ? err.customMessage : ERR_VERSION_HISTORY_LOAD, true);
            }
        })();
    }, [latestSavedItemSnapshot, dispatch]);

    useEffect(() => loadHTMLForLatestSavedItem(), [loadHTMLForLatestSavedItem]);

    useEffect(() => {
        dispatch(
            htmlOutputActions.updatedHtmlBody({
                html: currentItem.htmlWithEmbeddedCustomFonts,
                htmlWithEmbeddedCustomFonts: currentItem.htmlWithEmbeddedCustomFonts,
            })
        );
    }, [currentItem, dispatch]);

    useEffect(() => () => dispatch(versionHistoryActions.updateVersionHistoryItemLoadedStatus(false)), [dispatch]);

    const loadVersionHistoryItem = async (versionID) => {
        try {
            const cachedVersion = cachedVersions[versionID];
            if (cachedVersion) {
                dispatch(versionHistoryActions.updateVersionHistoryItem(cachedVersion));
                return;
            }

            dispatch(htmlOutputActions.updatedLoadedStatus(false));

            const { blocks } = await request.get(
                `${configs.versionHistoryItem}?documentID=${documentID}&versionID=${versionID}`
            );
            const { htmlWithEmbeddedCustomFonts } = await request.convertHtml({
                draftID: draftTemplateID,
                blocks: JSON.parse(blocks),
                useEmbeddedCustomFonts: true,
            });

            const version = { blocks: JSON.parse(blocks), htmlWithEmbeddedCustomFonts };
            setCachedVersions({
                ...cachedVersions,
                [versionID]: version,
            });

            dispatch(versionHistoryActions.updateVersionHistoryItem(version));
        } catch (err) {
            console.error(err);
            eventService.notify(err.customMessage ? err.customMessage : ERR_VERSION_HISTORY_LOAD, true);
        }
    };

    const { loaded, bodyWithEmbeddedCustomFonts } = htmlOutput;

    const restoreVersion = () => {
        const { blocks } = currentItem;
        if (blocks) {
            dispatch(canvasActions.populateInitialBlocks({ initialBlocksState: currentItem.blocks }));
            resetVersionHistory();
        }
    };

    return (
        <Styled.VersionHistoryDiv>
            <Styled.LeftPanelOuterDiv>
                <VersionHistoryLeftPanel loadVersionHistoryItem={loadVersionHistoryItem} />
            </Styled.LeftPanelOuterDiv>
            <Styled.CenterPanelDiv>
                <Styled.ButtonDiv isCurrentVersion={isCurrentVersion}>
                    <BananatagButton
                        text={isCurrentVersion ? 'Current Version' : 'Restore This Version'}
                        ariaLabel={`${isCurrentVersion ? 'Current' : 'Restore this'} version from version history`}
                        onClick={() => (hasOtherConnectedClients ? setShowWarningModal(true) : restoreVersion())}
                        disabled={isCurrentVersion}
                        aria-disabled={isCurrentVersion}
                        type="solid"
                        size="medium"
                    />
                </Styled.ButtonDiv>
                <Styled.LoadingDiv>
                    {!loaded && (
                        <>
                            <Styled.LoadingImg
                                width="200"
                                src="https://app-data-legacy.bananatag.com/pikasso/assets/loader/banana-loader.gif"
                                alt="loader"
                            />
                            <Styled.LoadingText>Loading...</Styled.LoadingText>
                        </>
                    )}
                </Styled.LoadingDiv>
                <Styled.PreviewDiv>
                    <Styled.PreviewInnerDiv>
                        {loaded && <PreviewIframe body={bodyWithEmbeddedCustomFonts} previewWidth="100%" />}
                    </Styled.PreviewInnerDiv>
                </Styled.PreviewDiv>
            </Styled.CenterPanelDiv>
            {showWarningModal && (
                <VersionHistoryWarningModal
                    closeWarningModal={() => setShowWarningModal(false)}
                    restoreVersion={restoreVersion}
                />
            )}
        </Styled.VersionHistoryDiv>
    );
};

const mapStateToProps = (state) => ({
    documentID:
        (_get(state, 'session.collaboration.type', '') === 'template' ? 't' : 'd') +
        _get(state, 'session.collaboration.id', '') +
        _get(state, 'session.collaboration.versionID', '') +
        _get(state, 'session.collaboration.trackID', ''),
    draftTemplateID: _get(state, 'session.collaboration.id'),
    currentItem: _get(state, 'versionHistory.versionHistoryItem', {}),
    latestSavedItem: _get(state, 'versionHistory.latestVersionHistorySavedItem', {}),
    isCurrentVersion: areBlocksEqual(state.versionHistory.versionHistoryItem.blocks, state.canvasBlocks.present),
    hasOtherConnectedClients: _get(state, 'session.collaboration.connectedClients', []).length > 1,
    htmlOutput: _get(state, 'htmlOutput', ''),
});

VersionHistory.propTypes = {
    documentID: PropTypes.string.isRequired,
    draftTemplateID: PropTypes.string.isRequired,
    currentItem: PropTypes.object.isRequired,
    latestSavedItem: PropTypes.object.isRequired,
    isCurrentVersion: PropTypes.bool.isRequired,
    hasOtherConnectedClients: PropTypes.bool.isRequired,
    htmlOutput: PropTypes.object.isRequired,
    resetVersionHistory: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(VersionHistory);
